import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, ChevronRight, HelpCircle, Monitor, Plus, RotateCcw, Target, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE DETECTION HOOK
// ─────────────────────────────────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);
  return isMobile;
};

// ─────────────────────────────────────────────────────────────────────────────
// MOBILE GATE — bannière non-bloquante
// ─────────────────────────────────────────────────────────────────────────────
const MobileGate = ({ onDismiss, onContinue }) => (
  <motion.div
    initial={{ y: -80, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    exit={{ y: -80, opacity: 0 }}
    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    className="fixed top-0 left-0 right-0 z-[200] px-4 pt-4 pb-3"
    style={{ background: 'linear-gradient(to bottom, #0f172a 80%, transparent)' }}
  >
    <div className="max-w-2xl mx-auto flex items-start gap-3 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 backdrop-blur-xl">
      <Monitor size={18} className="text-amber-400 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-amber-200 leading-snug mb-1">
          Meilleure expérience sur ordinateur
        </p>
        <p className="text-xs text-amber-300/70 leading-relaxed">
          Cette cartographie nécessite de déplacer des éléments sur une carte. Sur mobile, certaines interactions peuvent être imprécises.
        </p>
        <div className="flex items-center gap-3 mt-3">
          <button
            onClick={onContinue}
            className="text-xs font-semibold text-amber-300 hover:text-amber-100 transition-colors underline underline-offset-2"
          >
            Continuer quand même
          </button>
          <span className="text-amber-500/40">·</span>
          <button
            onClick={onDismiss}
            className="text-xs font-semibold text-white/60 hover:text-white transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  </motion.div>
);

// ─────────────────────────────────────────────────────────────────────────────
// CONTEXTUAL GUIDANCE — bandeau réactif, pas de modal
// ─────────────────────────────────────────────────────────────────────────────
const GUIDANCE_STATES = {
  empty: {
    text: 'Commencez par ajouter un premier profil — une personne réelle dont vous connaissez le parcours.',
    cta: 'Ajouter un profil',
    color: 'cyan',
  },
  one: {
    text: 'Bien. Ajoutez au moins deux autres profils pour faire émerger des contrastes.',
    cta: 'Ajouter un autre profil',
    color: 'cyan',
  },
  few: {
    text: 'Maintenant, positionnez chaque profil sur la carte. Faites glisser les pastilles selon les capitaux de chaque personne.',
    cta: null,
    color: 'emerald',
  },
  positioned: {
    text: 'Observez les écarts. Cliquez sur un profil pour analyser sa trajectoire et identifier d\'éventuelles tensions.',
    cta: null,
    color: 'amber',
  },
  analysed: {
    text: 'Analyse complète. Vous pouvez marquer les tensions symboliques ou exporter votre cartographie.',
    cta: null,
    color: 'purple',
  },
};

const GuidanceBanner = ({ state, profiles, onAddProfile }) => {
  const [dismissed, setDismissed] = useState(false);
  const [prevState, setPrevState] = useState(state);

  useEffect(() => {
    if (state !== prevState) {
      setDismissed(false);
      setPrevState(state);
    }
  }, [state, prevState]);

  const g = GUIDANCE_STATES[state] || GUIDANCE_STATES.empty;

  const colorMap = {
    cyan:    { border: 'border-cyan-500/30',   bg: 'bg-cyan-500/10',   text: 'text-cyan-300',   btn: 'bg-cyan-500 hover:bg-cyan-400 text-black' },
    emerald: { border: 'border-emerald-500/30', bg: 'bg-emerald-500/10', text: 'text-emerald-300', btn: 'bg-emerald-500 hover:bg-emerald-400 text-black' },
    amber:   { border: 'border-amber-500/30',   bg: 'bg-amber-500/10',   text: 'text-amber-300',   btn: 'bg-amber-500 hover:bg-amber-400 text-black' },
    purple:  { border: 'border-purple-500/30',  bg: 'bg-purple-500/10',  text: 'text-purple-300',  btn: 'bg-purple-500 hover:bg-purple-400 text-white' },
  };
  const c = colorMap[g.color];

  if (dismissed) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={state}
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.25 }}
        className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${c.border} ${c.bg} mb-4`}
      >
        <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${
          g.color === 'cyan' ? 'bg-cyan-400 animate-pulse' :
          g.color === 'emerald' ? 'bg-emerald-400 animate-pulse' :
          g.color === 'amber' ? 'bg-amber-400' : 'bg-purple-400'
        }`} />
        <p className={`flex-1 text-xs leading-relaxed ${c.text}`}>{g.text}</p>
        {g.cta && (
          <button
            onClick={onAddProfile}
            className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${c.btn}`}
          >
            {g.cta}
          </button>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 p-1 rounded hover:bg-white/10 transition-colors"
        >
          <X size={12} className="text-white/30" />
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GHOST PROFILES — profils fantômes pour calibrer visuellement la carte
// ─────────────────────────────────────────────────────────────────────────────
const GHOST_PROFILES = [
  { id: 'ghost-1', name: 'Exemple : médecin', x: 72, y: 22, description: 'Capital culturel et économique élevés' },
  { id: 'ghost-2', name: 'Exemple : ouvrier', x: 28, y: 75, description: 'Capital culturel et économique faibles' },
  { id: 'ghost-3', name: 'Exemple : enseignant', x: 35, y: 28, description: 'Capital culturel élevé, économique moyen' },
];

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE FORM — formulaire intégré, sans modal bloquant
// ─────────────────────────────────────────────────────────────────────────────
const ProfileForm = ({ onSubmit, onCancel, index }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [origin, setOrigin] = useState('');
  const nameRef = useRef(null);

  useEffect(() => { nameRef.current?.focus(); }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim(), origin: origin.trim() });
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.2 }}
      className="rounded-2xl border border-cyan-500/30 bg-[#0a1628]/95 backdrop-blur-xl p-6 shadow-2xl"
    >
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-white">Nouveau profil #{index}</h3>
          <p className="text-xs text-slate-500 mt-0.5">Une personne réelle dont vous connaissez le parcours</p>
        </div>
        <button onClick={onCancel} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
          <X size={14} className="text-slate-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Nom ou pseudonyme <span className="text-cyan-500">*</span>
          </label>
          <input
            ref={nameRef}
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Ex : Marie D., mon père, Samir..."
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500/60 focus:bg-cyan-500/5 outline-none transition-all placeholder:text-slate-600 text-white"
            required
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Résumé du parcours
          </label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Formation, profession, mobilité sociale..."
            rows={2}
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500/60 focus:bg-cyan-500/5 outline-none transition-all resize-none placeholder:text-slate-600 text-white"
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
            Milieu d'origine
          </label>
          <input
            value={origin}
            onChange={e => setOrigin(e.target.value)}
            placeholder="Ex : famille ouvrière, parents agriculteurs..."
            className="w-full px-3 py-2.5 text-sm rounded-xl bg-white/5 border border-white/10 focus:border-cyan-500/60 focus:bg-cyan-500/5 outline-none transition-all placeholder:text-slate-600 text-white"
          />
        </div>

        <div className="flex gap-3 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2.5 text-xs font-semibold text-slate-500 hover:text-white transition-colors rounded-xl border border-white/10 hover:border-white/20"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={!name.trim()}
            className="flex-[2] py-2.5 text-xs font-semibold rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-cyan-500 hover:bg-cyan-400 text-black"
          >
            Ajouter ce profil
          </button>
        </div>
      </form>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE NODE — pastille draggable sur la carte
// ─────────────────────────────────────────────────────────────────────────────
const ProfileNode = ({ profile, isSelected, onSelect, onPositionChange, mapRef, isMobile }) => {
  const nodeRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnd = useCallback((event, info) => {
    setIsDragging(false);
    if (!mapRef.current) return;
    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.max(4, Math.min(96, ((info.point.x - rect.left) / rect.width) * 100));
    const y = Math.max(4, Math.min(96, ((info.point.y - rect.top) / rect.height) * 100));
    onPositionChange(profile.id, x, y);
  }, [mapRef, onPositionChange, profile.id]);

  const colors = [
    { ring: '#22d3ee', bg: '#0e7490', glow: 'rgba(34,211,238,0.4)' },
    { ring: '#f59e0b', bg: '#b45309', glow: 'rgba(245,158,11,0.4)' },
    { ring: '#a78bfa', bg: '#7c3aed', glow: 'rgba(167,139,250,0.4)' },
    { ring: '#34d399', bg: '#059669', glow: 'rgba(52,211,153,0.4)' },
    { ring: '#f87171', bg: '#dc2626', glow: 'rgba(248,113,113,0.4)' },
    { ring: '#fb923c', bg: '#ea580c', glow: 'rgba(251,146,60,0.4)' },
  ];
  const color = colors[profile.colorIndex % colors.length];

  return (
    <motion.div
      ref={nodeRef}
      className="absolute"
      style={{ left: `${profile.x}%`, top: `${profile.y}%`, translateX: '-50%', translateY: '-50%', zIndex: isSelected ? 50 : isDragging ? 60 : 30 }}
      drag
      dragMomentum={false}
      dragElastic={0}
      onDragStart={() => setIsDragging(true)}
      onDragEnd={handleDragEnd}
      whileHover={{ scale: 1.08 }}
      whileDrag={{ scale: 1.15, zIndex: 60 }}
    >
      {/* Pulse ring on hover/select */}
      <AnimatePresence>
        {(isSelected || isDragging) && (
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1.6, opacity: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1, repeat: Infinity }}
            className="absolute inset-0 rounded-full"
            style={{ background: color.glow }}
          />
        )}
      </AnimatePresence>

      <button
        onClick={() => onSelect(profile.id)}
        className="relative w-11 h-11 rounded-full flex items-center justify-center transition-all cursor-grab active:cursor-grabbing"
        style={{
          background: color.bg,
          border: `2px solid ${isSelected ? color.ring : 'rgba(255,255,255,0.2)'}`,
          boxShadow: isSelected ? `0 0 0 3px ${color.glow}, 0 4px 16px ${color.glow}` : '0 2px 8px rgba(0,0,0,0.4)',
        }}
        title={`${profile.name} — cliquez pour analyser, glissez pour repositionner`}
      >
        <span className="text-[10px] font-bold text-white select-none">
          {profile.name.slice(0, 2).toUpperCase()}
        </span>
        {profile.hasViolence && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center border border-[#020617]">
            <AlertTriangle size={8} className="text-white" />
          </div>
        )}
      </button>

      {/* Label sous la pastille */}
      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 pointer-events-none">
        <div className={`px-2 py-0.5 rounded-md text-[9px] font-semibold whitespace-nowrap transition-all ${
          isSelected ? 'bg-white text-black' : 'bg-black/60 text-white/70 backdrop-blur-sm'
        }`}>
          {profile.name.length > 14 ? profile.name.slice(0, 14) + '…' : profile.name}
        </div>
      </div>

      {/* Hint drag au premier usage */}
      {profile.isNew && !isDragging && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: [0, 1, 1, 0], y: [4, 0, 0, -4] }}
          transition={{ duration: 2.5, delay: 0.5, times: [0, 0.2, 0.8, 1] }}
          className="absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap pointer-events-none"
        >
          <span className="text-[9px] text-cyan-400/80 font-medium">Glissez pour positionner</span>
        </motion.div>
      )}
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PROFILE DETAIL PANEL — panneau d'analyse latéral, non-bloquant
// ─────────────────────────────────────────────────────────────────────────────
const ProfileDetailPanel = ({ profile, onClose, onToggleViolence, onDelete }) => {
  if (!profile) return null;

  const quadrantLabel = (x, y) => {
    if (x >= 50 && y <= 50) return { label: 'Dominants', desc: 'Capital économique et culturel élevés', color: 'amber' };
    if (x < 50 && y <= 50)  return { label: 'Intellectuels précaires', desc: 'Capital culturel élevé, économique faible', color: 'cyan' };
    if (x >= 50 && y > 50)  return { label: 'Petite bourgeoisie', desc: 'Capital économique élevé, culturel faible', color: 'emerald' };
    return { label: 'Classes populaires', desc: 'Capital économique et culturel faibles', color: 'slate' };
  };

  const q = quadrantLabel(profile.x, profile.y);
  const colorMap = {
    amber:   { badge: 'bg-amber-500/15 text-amber-300 border-amber-500/30' },
    cyan:    { badge: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30' },
    emerald: { badge: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    slate:   { badge: 'bg-slate-500/15 text-slate-300 border-slate-500/30' },
  };

  return (
    <motion.div
      initial={{ x: 40, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 40, opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="w-72 shrink-0 rounded-2xl border border-white/10 bg-[#0a1628]/95 backdrop-blur-xl flex flex-col overflow-hidden shadow-2xl"
    >
      {/* Header */}
      <div className="px-5 py-4 border-b border-white/[0.06] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-white truncate">{profile.name}</h3>
          {profile.origin && (
            <p className="text-[10px] text-slate-500 mt-0.5 truncate">Origine : {profile.origin}</p>
          )}
        </div>
        <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10 transition-colors shrink-0">
          <X size={14} className="text-slate-500" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {/* Position dans le champ */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Position dans le champ</p>
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-medium ${colorMap[q.color].badge}`}>
            <div className="w-1.5 h-1.5 rounded-full bg-current" />
            {q.label}
          </div>
          <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">{q.desc}</p>
        </div>

        {/* Coordonnées */}
        <div className="grid grid-cols-2 gap-2">
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p className="text-[9px] text-slate-600 uppercase tracking-wider mb-1">Capital culturel</p>
            <p className="text-lg font-semibold text-white">{Math.round(100 - profile.y)}<span className="text-sm text-slate-500">/100</span></p>
          </div>
          <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <p className="text-[9px] text-slate-600 uppercase tracking-wider mb-1">Capital économique</p>
            <p className="text-lg font-semibold text-white">{Math.round(profile.x)}<span className="text-sm text-slate-500">/100</span></p>
          </div>
        </div>

        {/* Description */}
        {profile.description && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Parcours</p>
            <p className="text-xs text-slate-300 leading-relaxed">{profile.description}</p>
          </div>
        )}

        {/* Violence symbolique */}
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Violence symbolique</p>
          <button
            onClick={() => onToggleViolence(profile.id)}
            className={`w-full py-2.5 px-4 rounded-xl text-xs font-semibold transition-all border flex items-center justify-center gap-2 ${
              profile.hasViolence
                ? 'bg-red-500/20 border-red-500/40 text-red-300 hover:bg-red-500/30'
                : 'bg-white/[0.03] border-white/10 text-slate-400 hover:bg-white/[0.06] hover:text-white'
            }`}
          >
            <AlertTriangle size={12} />
            {profile.hasViolence ? 'Tension marquée — retirer' : 'Marquer une tension'}
          </button>
          {profile.hasViolence && (
            <motion.p
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-[10px] text-red-400/70 leading-relaxed mt-2"
            >
              Ce profil présente une tension sociale notable dans sa trajectoire.
            </motion.p>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 py-4 border-t border-white/[0.06]">
        <button
          onClick={() => onDelete(profile.id)}
          className="w-full py-2 text-[10px] font-semibold text-slate-600 hover:text-red-400 transition-colors"
        >
          Supprimer ce profil
        </button>
      </div>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QUADRANT LABELS — étiquettes des 4 quadrants de la carte
// ─────────────────────────────────────────────────────────────────────────────
const QuadrantLabels = ({ config }) => {
  const quadrants = config?.quadrants || [
    { name: 'Dominants', color: '#f59e0b', corner: 'top-right' },
    { name: 'Intellectuels précaires', color: '#22d3ee', corner: 'top-left' },
    { name: 'Petite bourgeoisie', color: '#34d399', corner: 'bottom-right' },
    { name: 'Classes populaires', color: '#94a3b8', corner: 'bottom-left' },
  ];

  const posMap = {
    'top-right':    'top-4 right-4 text-right',
    'top-left':     'top-4 left-4 text-left',
    'bottom-right': 'bottom-4 right-4 text-right',
    'bottom-left':  'bottom-4 left-4 text-left',
  };

  return (
    <>
      {quadrants.map((q, i) => (
        <div key={i} className={`absolute ${posMap[q.corner]} pointer-events-none`}>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.15em]"
            style={{ color: q.color, opacity: 0.35 }}
          >
            {q.name}
          </span>
        </div>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// LEGEND / AXIS LABELS
// ─────────────────────────────────────────────────────────────────────────────
const AxisLabels = () => (
  <>
    {/* Vertical axis label — Capital culturel */}
    <div className="absolute -left-6 top-1/2 -translate-y-1/2 -rotate-90 pointer-events-none">
      <div className="flex items-center gap-6 whitespace-nowrap">
        <span className="text-[9px] uppercase tracking-widest text-white/20">− Capital culturel</span>
        <div className="w-12 h-px bg-white/10" />
        <span className="text-[9px] uppercase tracking-widest text-white/20">Capital culturel +</span>
      </div>
    </div>
    {/* Horizontal axis label — Capital économique */}
    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-6 pointer-events-none flex items-center gap-6">
      <span className="text-[9px] uppercase tracking-widest text-white/20">− Capital économique</span>
      <div className="w-12 h-px bg-white/10" />
      <span className="text-[9px] uppercase tracking-widest text-white/20">Capital économique +</span>
    </div>
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// COMPUTE GUIDANCE STATE
// ─────────────────────────────────────────────────────────────────────────────
const computeGuidanceState = (profiles) => {
  if (profiles.length === 0) return 'empty';
  if (profiles.length === 1) return 'one';
  const movedCount = profiles.filter(p => !p.isNew).length;
  const violenceCount = profiles.filter(p => p.hasViolence).length;
  if (violenceCount > 0) return 'analysed';
  if (movedCount >= Math.ceil(profiles.length / 2)) return 'positioned';
  return 'few';
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const SocialFieldMapper = ({ config = {} }) => {
  const isMobile = useIsMobile();
  const [showMobileGate, setShowMobileGate] = useState(true);
  const [mobileAcknowledged, setMobileAcknowledged] = useState(false);

  const [profiles, setProfiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [colorIndex, setColorIndex] = useState(0);

  const mapRef = useRef(null);
  const minProfiles = config.minTrajectories || 3;

  // Mobile gate logic
  useEffect(() => {
    if (!isMobile) setShowMobileGate(false);
  }, [isMobile]);

  const guidanceState = computeGuidanceState(profiles);
  const selectedProfile = profiles.find(p => p.id === selectedId) || null;
  const showGhosts = profiles.length === 0;

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleAddProfile = useCallback((data) => {
    const newProfile = {
      id: `p-${Date.now()}`,
      ...data,
      x: 48 + (Math.random() - 0.5) * 20,
      y: 48 + (Math.random() - 0.5) * 20,
      hasViolence: false,
      isNew: true,
      colorIndex,
    };
    setProfiles(prev => [...prev, newProfile]);
    setColorIndex(prev => prev + 1);
    setShowForm(false);
    setSelectedId(newProfile.id);

    // Remove isNew flag after drag hint fades
    setTimeout(() => {
      setProfiles(prev => prev.map(p => p.id === newProfile.id ? { ...p, isNew: false } : p));
    }, 4000);
  }, [colorIndex]);

  const handlePositionChange = useCallback((id, x, y) => {
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, x, y, isNew: false } : p));
  }, []);

  const handleToggleViolence = useCallback((id) => {
    setProfiles(prev => prev.map(p => p.id === id ? { ...p, hasViolence: !p.hasViolence } : p));
  }, []);

  const handleDelete = useCallback((id) => {
    setProfiles(prev => prev.filter(p => p.id !== id));
    setSelectedId(null);
  }, []);

  const handleAutoArrange = useCallback(() => {
    const count = profiles.length;
    if (count === 0) return;
    const cols = Math.ceil(Math.sqrt(count));
    setProfiles(prev => prev.map((p, i) => ({
      ...p,
      x: 20 + (i % cols) * (60 / Math.max(cols - 1, 1)),
      y: 20 + Math.floor(i / cols) * (60 / Math.max(Math.ceil(count / cols) - 1, 1)),
      isNew: false,
    })));
  }, [profiles.length]);

  const handleReset = () => {
    if (profiles.length === 0) return;
    if (window.confirm('Effacer tous les profils et recommencer ?')) {
      setProfiles([]);
      setSelectedId(null);
      setColorIndex(0);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-4 relative">
      {/* Mobile gate */}
      <AnimatePresence>
        {isMobile && showMobileGate && !mobileAcknowledged && (
          <MobileGate
            onDismiss={() => setShowMobileGate(false)}
            onContinue={() => { setMobileAcknowledged(true); setShowMobileGate(false); }}
          />
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-white">
            {config.title || 'Cartographie du champ social'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {profiles.length} profil{profiles.length !== 1 ? 's' : ''} cartographié{profiles.length !== 1 ? 's' : ''}
            {profiles.length > 0 && profiles.length < minProfiles && (
              <span className="text-cyan-500/70"> · encore {minProfiles - profiles.length} recommandé{minProfiles - profiles.length > 1 ? 's' : ''}</span>
            )}
            {profiles.length >= minProfiles && (
              <span className="text-emerald-500/70"> · minimum atteint</span>
            )}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {profiles.length > 1 && (
            <button
              onClick={handleAutoArrange}
              title="Répartir automatiquement"
              className="p-2 rounded-xl border border-white/10 hover:border-white/20 bg-white/[0.03] hover:bg-white/[0.06] text-slate-500 hover:text-white transition-all"
            >
              <Target size={15} />
            </button>
          )}
          <button
            onClick={handleReset}
            title="Recommencer"
            className="p-2 rounded-xl border border-white/10 hover:border-red-500/30 bg-white/[0.03] hover:bg-red-500/[0.05] text-slate-500 hover:text-red-400 transition-all"
          >
            <RotateCcw size={15} />
          </button>
          <button
            onClick={() => setShowHelp(!showHelp)}
            title="Aide"
            className={`p-2 rounded-xl border transition-all ${showHelp ? 'border-cyan-500/40 bg-cyan-500/10 text-cyan-400' : 'border-white/10 bg-white/[0.03] text-slate-500 hover:text-white'}`}
          >
            <HelpCircle size={15} />
          </button>
        </div>
      </div>

      {/* Guidance banner — réactif à l'état */}
      <GuidanceBanner
        state={guidanceState}
        profiles={profiles}
        onAddProfile={() => setShowForm(true)}
      />

      {/* Help panel — contextuel, non bloquant */}
      <AnimatePresence>
        {showHelp && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="p-4 rounded-xl border border-white/10 bg-white/[0.02] space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'Axe vertical', desc: 'Capital culturel — diplômes, savoirs, pratiques culturelles. En haut = capital culturel élevé.' },
                { label: 'Axe horizontal', desc: 'Capital économique — revenus, patrimoine, ressources. À droite = capital économique élevé.' },
                { label: 'Déplacer un profil', desc: 'Cliquez et faites glisser la pastille vers sa position dans le champ.' },
                { label: 'Violence symbolique', desc: 'Cliquez sur un profil, puis "Marquer une tension" pour identifier les inégalités vécues.' },
              ].map((h, i) => (
                <div key={i} className="flex gap-2.5">
                  <ChevronRight size={12} className="text-cyan-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-white">{h.label}</p>
                    <p className="text-[11px] text-slate-500 leading-relaxed mt-0.5">{h.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main layout */}
      <div className="flex gap-4">
        {/* Map area */}
        <div className="flex-1 min-w-0">
          {/* Form (inline above map) */}
          <AnimatePresence>
            {showForm && (
              <div className="mb-4">
                <ProfileForm
                  onSubmit={handleAddProfile}
                  onCancel={() => setShowForm(false)}
                  index={profiles.length + 1}
                />
              </div>
            )}
          </AnimatePresence>

          {/* Map container */}
          <div
            ref={mapRef}
            className="relative rounded-2xl overflow-hidden border border-white/[0.06] bg-[#020b18]"
            style={{ height: isMobile ? '55vw' : '520px', minHeight: 280 }}
          >
            {/* Grid */}
            <div
              className="absolute inset-0 opacity-[0.04] pointer-events-none"
              style={{
                backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)',
                backgroundSize: '48px 48px',
              }}
            />

            {/* Axes — lignes centrales */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute left-0 right-0 top-1/2 h-px bg-white/[0.07]" />
              <div className="absolute top-0 bottom-0 left-1/2 w-px bg-white/[0.07]" />
            </div>

            {/* Quadrant shading subtil */}
            <div className="absolute inset-0 pointer-events-none grid grid-cols-2 grid-rows-2">
              <div className="border-r border-b border-white/[0.025]" />
              <div className="border-b border-white/[0.025]" />
              <div className="border-r border-white/[0.025]" />
              <div />
            </div>

            {/* Quadrant labels */}
            <QuadrantLabels config={config} />

            {/* Axis labels */}
            <AxisLabels />

            {/* Ghost profiles (quand la carte est vide) */}
            <AnimatePresence>
              {showGhosts && GHOST_PROFILES.map(ghost => (
                <motion.div
                  key={ghost.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ delay: 0.3 }}
                  className="absolute pointer-events-none"
                  style={{ left: `${ghost.x}%`, top: `${ghost.y}%`, translateX: '-50%', translateY: '-50%' }}
                >
                  <div className="w-11 h-11 rounded-full border-2 border-dashed border-white/15 flex items-center justify-center bg-white/[0.02]">
                    <Target size={16} className="text-white/15" />
                  </div>
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 whitespace-nowrap">
                    <span className="text-[9px] text-white/20 font-medium">{ghost.name}</span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Real profiles */}
            {profiles.map(profile => (
              <ProfileNode
                key={profile.id}
                profile={profile}
                isSelected={selectedId === profile.id}
                onSelect={(id) => setSelectedId(prev => prev === id ? null : id)}
                onPositionChange={handlePositionChange}
                mapRef={mapRef}
                isMobile={isMobile}
              />
            ))}

            {/* Empty state CTA au centre */}
            {profiles.length === 0 && !showForm && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
              >
                <div className="text-center">
                  <p className="text-xs text-white/20 font-medium">Carte vide</p>
                  <p className="text-[10px] text-white/10 mt-1">Ajoutez des profils pour commencer</p>
                </div>
              </motion.div>
            )}
          </div>

          {/* Add profile button — sous la carte */}
          {!showForm && (
            <motion.div layout className="mt-3">
              <button
                onClick={() => setShowForm(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-500/30 bg-cyan-500/10 hover:bg-cyan-500/20 hover:border-cyan-500/50 text-cyan-400 text-xs font-semibold transition-all w-full justify-center"
              >
                <Plus size={14} />
                Ajouter un profil
                {profiles.length > 0 && (
                  <span className="ml-auto text-[10px] text-cyan-500/50">#{profiles.length + 1}</span>
                )}
              </button>
            </motion.div>
          )}
        </div>

        {/* Detail panel — latéral, non-bloquant */}
        <AnimatePresence>
          {selectedProfile && (
            <ProfileDetailPanel
              profile={selectedProfile}
              onClose={() => setSelectedId(null)}
              onToggleViolence={handleToggleViolence}
              onDelete={handleDelete}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Progress indicator discret — en bas */}
      {profiles.length > 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center gap-3 px-1"
        >
          <div className="flex gap-1">
            {Array.from({ length: Math.max(minProfiles, profiles.length) }).map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i < profiles.length
                    ? profiles[i]?.hasViolence ? 'bg-red-500 w-6' : 'bg-cyan-500 w-6'
                    : 'bg-white/10 w-3'
                }`}
              />
            ))}
          </div>
          <span className="text-[10px] text-slate-600">
            {profiles.filter(p => p.hasViolence).length > 0
              ? `${profiles.filter(p => p.hasViolence).length} tension${profiles.filter(p => p.hasViolence).length > 1 ? 's' : ''} identifiée${profiles.filter(p => p.hasViolence).length > 1 ? 's' : ''}`
              : `${profiles.length} / ${minProfiles} profils minimum`
            }
          </span>
        </motion.div>
      )}
    </div>
  );
};

export default SocialFieldMapper;